import React, { useEffect, useState } from 'react';
import { ethers } from 'ethers';
import Input from '../../components/common/Input/Input';
import Button from '../../components/common/Button/Button';
import { useWallet } from '../../context/WalletContext';
import { useNotification } from '../../context/NotificationContext';
import { formatNumber, shortenAddress } from '../../utils/formatters';
import { Section, InfoRow, StatGrid, Loading } from './AdminUI';
import { fromWei, toWei, pctToBps, bpsToPct } from './useAdmin';
import { ROUTER_FEE_ID } from '../../config/contract';

// OWNER ONLY - rendered by Admin.jsx only when isOwner is true
const AdminOwner = ({ admin }) => {
  const { read, router, runTx, pending, refreshRole } = admin;
  const { account } = useWallet();
  const { error: showError } = useNotification();

  const [pool, setPool] = useState(null);
  const [roles, setRoles] = useState(null);
  const [routerPct, setRouterPct] = useState('');
  const [poolAmt, setPoolAmt] = useState('');
  const [poolDest, setPoolDest] = useState('');
  const [newAdmin, setNewAdmin] = useState('');
  const [newOwner, setNewOwner] = useState('');

  const load = async () => {
    if (!router) return;
    try {
      const [amount, enabled, pct, ownerAddr, adminAddr, logic] = await Promise.all([
        router.readStorage.routerPool(),
        router.readStorage.routerPoolEnabled(),
        router.readStorage.routerPoolPercent(),
        read.storage.owner(),
        read.storage.admin(),
        read.storage.logicContract()
      ]);
      setPool({ amount: fromWei(amount), enabled, pct: bpsToPct(pct) });
      setRouterPct(bpsToPct(pct));
      setRoles({ owner: ownerAddr, admin: adminAddr, logic });
    } catch (err) {
      console.error('Owner tab load error:', err);
    }
  };

  useEffect(() => { load(); }, [router, read]);
  useEffect(() => { if (account && !poolDest) setPoolDest(account); }, [account]);

  if (!router) return null;
  if (!pool || !roles) return <Loading />;

  const routerTx = (key, label, send) => runTx(key, label, () => send(router));

  const toggle = async () => {
    const ok = await routerTx('rp-toggle', pool.enabled ? 'Disable' : 'Enable', (r) => r.storage.toggleRouterPool(!pool.enabled));
    if (ok) load();
  };

  const savePct = async () => {
    const pct = parseFloat(routerPct);
    if (isNaN(pct) || pct < 0 || pct > 50) return showError('Percentage must be 0 - 50%');
    const ok = await routerTx('rp-pct', 'Pool % update', (r) => r.storage.setFeePercent(ROUTER_FEE_ID, pctToBps(pct)));
    if (ok) load();
  };

  const claim = async () => {
    const amt = parseFloat(poolAmt);
    if (isNaN(amt) || amt <= 0) return showError('Enter an amount');
    if (amt > parseFloat(pool.amount)) return showError('Amount exceeds pool balance');
    if (!ethers.utils.isAddress(poolDest)) return showError('Invalid destination address');
    if (!window.confirm(`Send ${poolAmt} USDT from the pool to ${shortenAddress(poolDest, 6)}?`)) return;
    const ok = await routerTx('rp-claim', 'Pool withdraw', (r) => r.logic.withdrawRouterPool(toWei(poolAmt), poolDest));
    if (ok) { setPoolAmt(''); load(); }
  };

  const roleTx = async (key, label, addr, warn, send) => {
    if (!ethers.utils.isAddress(addr) || addr === ethers.constants.AddressZero) {
      return showError('Enter a valid address');
    }
    if (!window.confirm(warn)) return;
    const ok = await runTx(key, label, send);
    if (ok) {
      await refreshRole();
      load();
    }
  };

  return (
    <>
      <StatGrid items={[
        { label: 'Pool Balance', value: `${formatNumber(pool.amount)} USDT` },
        { label: 'Status', value: pool.enabled ? 'Enabled' : 'Disabled' }
      ]} />

      <Section title="Pool Settings" subtitle={`${pool.pct}% of deposits + 5% of withdrawals are credited while enabled.`}>
        <Button
          fullWidth
          variant={pool.enabled ? 'danger' : 'success'}
          onClick={toggle}
          loading={pending === 'rp-toggle'}
        >
          {pool.enabled ? 'Disable Pool' : 'Enable Pool'}
        </Button>
        <Input label="Deposit share" type="number" suffix="%" value={routerPct} onChange={(e) => setRouterPct(e.target.value)} />
        <Button fullWidth variant="secondary" onClick={savePct} loading={pending === 'rp-pct'} disabled={routerPct === pool.pct}>
          Save Share
        </Button>
      </Section>

      <Section title="Claim Pool">
        <Input label="Amount" type="number" suffix="USDT" value={poolAmt} onChange={(e) => setPoolAmt(e.target.value)} />
        <button className="admin-chip" onClick={() => setPoolAmt(pool.amount)}>Max</button>
        <Input label="Destination" value={poolDest} onChange={(e) => setPoolDest(e.target.value.trim())} />
        <Button fullWidth onClick={claim} loading={pending === 'rp-claim'}>Claim</Button>
      </Section>

      <Section title="Roles" subtitle="Logic upgrades are in the Wiring tab." danger>
        <InfoRow label="Owner" value={shortenAddress(roles.owner, 6)} mono />
        <InfoRow label="Admin" value={shortenAddress(roles.admin, 6)} mono />
        <InfoRow label="Logic" value={shortenAddress(roles.logic, 6)} mono />

        <Input label="New admin" placeholder="0x..." value={newAdmin} onChange={(e) => setNewAdmin(e.target.value.trim())} />
        <Button
          fullWidth
          variant="secondary"
          loading={pending === 'set-admin'}
          onClick={() => roleTx('set-admin', 'Admin change', newAdmin,
            `Set ${newAdmin} as the new admin? The current admin loses access.`,
            (w) => w.storage.setAdmin(newAdmin))}
        >
          Change Admin
        </Button>

        <Input label="New owner" placeholder="0x..." value={newOwner} onChange={(e) => setNewOwner(e.target.value.trim())} />
        <Button
          fullWidth
          variant="danger"
          loading={pending === 'set-owner'}
          onClick={() => roleTx('set-owner', 'Ownership transfer', newOwner,
            `TRANSFER OWNERSHIP to ${newOwner}? You will lose owner access permanently. This cannot be undone.`,
            (w) => w.storage.transferOwnership(newOwner))}
        >
          Transfer Ownership
        </Button>
      </Section>
    </>
  );
};

export default AdminOwner;
